import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm, useWatch } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import {
  EQUIPMENT_PAYMENT_METHODS,
  EQUIPMENT_RECEIPT_METHODS,
  EQUIPMENT_PROCESS_TYPES,
  equipmentPurchaseSchema,
} from '@app/shared';
import type {
  EquipmentOption,
  EquipmentPaymentMethod,
  EquipmentProcessType,
  EquipmentRequestDto,
} from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { StatusChip } from '../../components/StatusChip';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { api } from '../../lib/api';
import { formatCents, formatDateTime } from '../../lib/format';
import { useAuth } from '../auth/AuthProvider';
import { useLookupOptions } from '../configuration/api';
import { withRequiredMessages } from '../inventory/formUtils';
import { useCustomerContext } from './CustomerLayout';
import { Section } from './parts';

interface PurchaseForm {
  customerId: string;
  equipmentId: string;
  processType: EquipmentProcessType;
  reasonId: string;
  method: EquipmentPaymentMethod | '';
  receiptNumber: string;
}

const COLUMNS: Column<EquipmentRequestDto>[] = [
  { key: 'equipment', header: 'Phone Model', render: (r) => r.equipment },
  { key: 'processType', header: 'Process Type', render: (r) => r.processType },
  { key: 'reason', header: 'Reason', render: (r) => r.reason },
  { key: 'amount', header: 'Amount', align: 'right', render: (r) => formatCents(r.amountCents) },
  { key: 'receipt', header: 'Receipt Number', render: (r) => r.receiptNumber },
  { key: 'status', header: 'Status', render: (r) => <StatusChip status={r.status} /> },
  {
    key: 'requestedAt',
    header: 'Request Date',
    nowrap: true,
    render: (r) => formatDateTime(r.requestedAt),
  },
  { key: 'tracking', header: 'Tracking Number', render: (r) => r.trackingNumber },
];

/**
 * Equipment Purchase (PC69), the reference's "CSR Screen For Adding Equipment". Built with the
 * two payment methods that need no gateway: Cash (PC409) and Free Equipment Purchase (PC572).
 * The request then waits under Shipment > Equipment & Accessories Request.
 */
export function EquipmentPurchasePage() {
  const { customer } = useCustomerContext();
  const { can } = useAuth();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const methods = EQUIPMENT_PAYMENT_METHODS.filter((m) => can(m.code));
  const equipment = useQuery({
    queryKey: ['equipment-requests', 'options'],
    queryFn: () => api.get<{ data: EquipmentOption[] }>('/equipment-requests/options'),
    select: (r) => r.data,
  });
  const reasons = useLookupOptions('equipmentStatusName');
  const requests = useQuery({
    queryKey: ['equipment-requests', 'customer', customer.id],
    queryFn: () =>
      api.get<{ data: EquipmentRequestDto[] }>('/equipment-requests', { customerId: customer.id }),
    select: (r) => r.data,
  });

  const empty: PurchaseForm = {
    customerId: customer.id,
    equipmentId: '',
    processType: 'Equipment Purchase',
    reasonId: '',
    method: methods[0]?.key ?? '',
    receiptNumber: '',
  };
  const { control, handleSubmit, reset, setError } = useForm<PurchaseForm>({
    defaultValues: empty,
    resolver: withRequiredMessages<PurchaseForm>(
      zodResolver(equipmentPurchaseSchema) as unknown as Resolver<PurchaseForm>,
      { equipmentId: 'Select Phone Model', method: 'Select a payment method' },
    ),
  });
  const [equipmentId, processType, method] = useWatch({
    control,
    name: ['equipmentId', 'processType', 'method'],
  });
  const chosen = (equipment.data ?? []).find((e) => e.id === equipmentId);
  const amount =
    !chosen || method === 'free' || processType === 'Resend Phone' ? 0 : chosen.priceCents;

  const purchase = useMutation({
    mutationFn: (input: PurchaseForm) =>
      api.post<EquipmentRequestDto>('/equipment-requests', input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['equipment-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['customers'] }),
      ]),
  });
  const submit = handleSubmit(async (values) => {
    try {
      const done = await purchase.mutateAsync(values);
      notify.success(
        `Equipment request created. ${formatCents(done.amountCents)} charged` +
          (done.taxCents ? ` (tax ${formatCents(done.taxCents)}).` : '.'),
      );
      reset(empty);
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <>
      <Section title="CSR Screen For Adding Equipment">
        <Box
          component="form"
          noValidate
          aria-label="Equipment Purchase"
          onSubmit={(e) => void submit(e)}
          sx={{ p: 1.5 }}
        >
          {methods.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No payment method is available to you. Each needs its own permission: Cash PC409,
              Money Order PC410, Money Gram PC411, Wallet PC412, Free Equipment Purchase PC572.
            </Alert>
          ) : null}
          <Controller
            control={control}
            name="method"
            render={({ field, fieldState }) => (
              <FormControl error={Boolean(fieldState.error)} sx={{ mb: 1.5 }}>
                <FormLabel id="equipment-method-label">Payment method</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="equipment-method-label"
                  name={field.name}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                >
                  {methods.map((m) => (
                    <FormControlLabel
                      key={m.key}
                      value={m.key}
                      label={`${m.label} (${m.code})`}
                      control={<Radio size="small" />}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          />
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              alignItems: 'start',
              maxWidth: 960,
            }}
          >
            <SelectInput
              control={control}
              name="equipmentId"
              label="Phone Model"
              required
              emptyLabel="Select"
              options={(equipment.data ?? []).map((e) => ({
                value: e.id,
                label: `${e.name} (${formatCents(e.priceCents)})`,
              }))}
              helperText={equipment.isError ? 'Could not load the equipment' : undefined}
            />
            <SelectInput
              control={control}
              name="processType"
              label="Process Type"
              options={toOptions(EQUIPMENT_PROCESS_TYPES)}
            />
            <SelectInput
              control={control}
              name="reasonId"
              label="Reason"
              emptyLabel="Select"
              options={(reasons.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
            />
            {EQUIPMENT_RECEIPT_METHODS.includes(method) ? (
              <TextInput
                control={control}
                name="receiptNumber"
                label="Receipt Number"
                required
                autoComplete="off"
                slotProps={{ htmlInput: { maxLength: 30 } }}
              />
            ) : null}
          </Box>
          <Typography sx={{ my: 1.5 }}>
            Price: <strong>{formatCents(amount)}</strong>. Tax is added when the order is placed, if
            equipment is taxed in the customer&apos;s state.
            {method === 'wallet'
              ? ` Wallet balance ${formatCents(customer.other.walletBalanceCents)}.`
              : ''}
          </Typography>
          <Button
            type="submit"
            variant="contained"
            disabled={purchase.isPending || methods.length === 0}
          >
            Submit
          </Button>
        </Box>
      </Section>
      <Section title="Equipment requests">
        <DataTable
          label="Equipment requests"
          columns={COLUMNS}
          rows={requests.data}
          rowKey={(r) => r.id}
          loading={requests.isFetching}
          error={requests.error}
          onRetry={() => void requests.refetch()}
          emptyText="This customer has no equipment request."
        />
      </Section>
    </>
  );
}
